import {
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from "lucide-react";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./Pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

import {cn} from "@/lib/utils";

export const PAGE_SIZE_OPTIONS = [10, 20, 50];
export const TEMPLATE_PAGE_SIZE_OPTIONS = [12, 24, 48];

interface TablePaginationProps {
  totalCount: number;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onFirstPage: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onLastPage: () => void;
  onPageSizeChange: (size: number) => void;
  rowsPerPageOptions?: number[];
  className?: string;
  hideRowsPerPage?: boolean;
}

export const TablePagination = ({
  totalCount,
  pageCount,
  pageIndex,
  pageSize,
  canPreviousPage,
  canNextPage,
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  onPageSizeChange,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  className,
  hideRowsPerPage = false,
}: TablePaginationProps) => {
  return (
    <Pagination
      className={cn(
        "h-[52px] p-1.5 w-full border-t border-border bg-background flex items-center justify-start",
        className,
      )}
    >
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={e => {
              e.preventDefault();
              onFirstPage();
            }}
            disabled={!canPreviousPage}
          >
            <span>
              <ChevronsLeft className="h-5 w-5 text-secondary" />
            </span>
          </PaginationPrevious>
        </PaginationItem>
        <PaginationItem>
          <PaginationPrevious
            onClick={e => {
              e.preventDefault();
              onPreviousPage();
            }}
            disabled={!canPreviousPage}
          >
            <ChevronLeft className="h-5 w-5 text-secondary" />
          </PaginationPrevious>
        </PaginationItem>
        <PaginationItem>
          <PaginationLink className="px-12">
            Page {pageIndex + 1} of {pageCount}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationNext
            onClick={e => {
              e.preventDefault();
              onNextPage();
            }}
            disabled={!canNextPage}
          >
            <ChevronRight className="h-5 w-5 text-secondary" />
          </PaginationNext>
        </PaginationItem>
        <PaginationItem>
          <PaginationNext
            onClick={e => {
              e.preventDefault();
              onLastPage();
            }}
            disabled={!canNextPage}
          >
            <span>
              <ChevronsRight className="h-5 w-5 text-secondary" />
            </span>
          </PaginationNext>
        </PaginationItem>
        {!hideRowsPerPage && (
          <div className="flex items-center gap-x-2 ml-1.5 border-l border-border pl-3 h-4 w-fit">
            <span className="text-slate-500 text-sm">Rows per page</span>
            <Select
              value={pageSize.toString()}
              onValueChange={value => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="w-fit min-w-[80px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent>
                {rowsPerPageOptions.map(size => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <p className="text-primary ml-1.5 border-l border-border pl-3 text-sm h-4 leading-4">
          {totalCount} {totalCount === 1 ? "result" : "results"}
        </p>
      </PaginationContent>
    </Pagination>
  );
};
